import { hasMinLength } from "@-/com.npmjs.ts-array-length";
import { isFreeTierGrapheme } from "@-/dev.improve.haniverse.lib.entitlement.free-tier";
import runes from "@-/com.npmjs.runes2";
import { useCallback } from "react";
import { useIsPremium } from "#use-is-premium";
import { LEARN_BEYOND_FREE_TIER_SUPERWALL_PLACEMENT } from "#superwall-placements";
import { alertPremiumUnavailable, useUpgradeToPremium } from "#use-upgrade-to-premium";

/**
 * Whether the viewer may add a given morpheme to study, plus the paywall that unblocks it.
 *
 * The free tier is "learn up to roughly HSK 1": a free user may add any grapheme introduced in the
 * curriculum levels through HSK 1 (TBCL 1 for traditional; see `FREE_TIER_CURRICULUM_GRADE`). Past
 * that, adding is what requires premium.
 *
 * Deliberately narrow — this gates ADDING A GRAPHEME only. Free users keep full access to:
 * - browsing every character in the app,
 * - reviewing every card already in their deck (including any added before this gate existed, or
 *   while they were subscribed — losing premium must never strand cards mid-schedule),
 * - all lessons within the free levels.
 *
 * This is a UI gate, not a security boundary. Premium mnemonic TEXT is stripped from the client
 * artifacts and served only by the authenticated worker route, which re-checks entitlement
 * server-side; nothing here protects bytes.
 *
 * `source` names the entry point for the paywall analytics events (see `useUpgradeToPremium`).
 */
export function useFreeTierGate(source: string): {
  canAddToStudy: (morpheme: string) => boolean;
  isPaywallPresented: boolean;
  openUpgradeToPremium: () => void;
} {
  const isPremium = useIsPremium();
  // Gated actions have no inline error slot, so an unavailable paywall is reported in an alert.
  const { isPaywallPresented, openUpgradeToPremium } = useUpgradeToPremium(
    LEARN_BEYOND_FREE_TIER_SUPERWALL_PLACEMENT,
    source,
    { onUnavailable: alertPremiumUnavailable },
  );

  const canAddToStudy = useCallback(
    (morpheme: string): boolean => {
      if (isPremium) {
        return true;
      }
      // WORDS are ungated. The curriculum is per-grapheme, so no level introduces a word and
      // `isFreeTierGrapheme` would be false for every one of them — gating on it would silently
      // make words premium-only, which is a product decision nobody made. Words are also not in
      // the bundled offline shards, so they are already an online-only feature. Revisit when a
      // word curriculum exists.
      if (hasMinLength(runes(morpheme), 2)) {
        return true;
      }
      return isFreeTierGrapheme(morpheme);
    },
    [isPremium],
  );

  return { canAddToStudy, isPaywallPresented, openUpgradeToPremium };
}
